
import type { ListingAddressInput } from '@shared/listing';

export interface PlaceSuggestion {
  placeId: string;
  description: string;
  primary?: string;
  secondary?: string;
}

export type PlaceDetails = ListingAddressInput;

const AUTOCOMPLETE_ENDPOINT = '/api/places/autocomplete';
const DETAILS_ENDPOINT = '/api/places/details';

async function placesGet<T>(path: string, params: Record<string, string>, signal?: AbortSignal): Promise<T> {
  const url = `${path}?${new URLSearchParams(params).toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json', 'x-cheddule-request': 'places' },
    signal,
  });
  if (!response.ok) {
    throw new PlacesUnavailableError(`Places proxy responded ${response.status}`);
  }
  return (await response.json()) as T;
}

export class PlacesUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlacesUnavailableError';
  }
}

export async function fetchPlaceSuggestions(
  query: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<PlaceSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];
  const data = await placesGet<{ suggestions?: PlaceSuggestion[] }>(
    AUTOCOMPLETE_ENDPOINT,
    { q, session: sessionToken },
    signal,
  );
  return data.suggestions ?? [];
}

export async function fetchPlaceDetails(
  placeId: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<PlaceDetails> {
  const data = await placesGet<{ address?: PlaceDetails }>(
    DETAILS_ENDPOINT,
    { placeId, session: sessionToken },
    signal,
  );
  if (!data.address) {
    throw new PlacesUnavailableError('Places details returned no address.');
  }
  return data.address;
}

export function newPlacesSessionToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
